import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';

import { STEPS } from './constants';
import useAuthenticated from '../../../hooks/useAuthenticated';
import useOpenAlert from '../../../hooks/useOpenAlert';
import useSpecialtyEventUpload from '../SpecialtyEventUploadView/hooks/useSpecialtyEventUpload';
import { AlbumForSpecialtyAlbumInvites } from '../../../graphql/queries/albums';
import { canViewSpecialtyEventAlbums } from '../SpecialtyEventUploadView/helpers';
import { isVideoAlbum, isWeddingsContent } from '../../../util/album';
import { isVideo } from '../../../apps/pro/components/UploadProgressTracking/helpers';
import { photoUploadFilter } from '../../../util/photo-upload';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import SpecialtyEventUploadLayout from '../SpecialtyEventUploadView/components/SpecialtyEventUploadLayout';
import PhotoUploadStep from '../SpecialtyEventUploadView/components/PhotoUploadStep';
import VideoUploadStep from '../SpecialtyEventUploadView/components/VideoUploadStep';
import UploadingStep from '../SpecialtyEventUploadView/components/UploadingStep';
import UploadSuccessStep from '../SpecialtyEventUploadView/components/UploadSuccessStep';
import HomeStep from './components/HomeStep';
import { getAsset } from '../../../util/assets';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
};

const StandaloneSpecialtyEventUploadView = ({ album, loading }) => {
  const [step, setStep] = useState(STEPS.LOADING);

  const authenticated = useAuthenticated();
  const openAlert = useOpenAlert();

  const { root_album_folder: albumFolder } = album ?? {};

  const {
    total,
    current,
    success,
    error,
    uploading,
    albumUploadReset,
    onHierarchyViewsCreate,
    onUpdateUploadedStates,
    onUpload,
    uploadedTargets,
  } = useSpecialtyEventUpload({
    authenticated,
    albumFolderId: albumFolder?.id,
  });

  useEffect(() => {
    albumUploadReset();
  }, []);

  useEffect(() => {
    if (album) {
      setStep(STEPS.HOME);
    }
  }, [album]);

  useEffect(() => {
    // Join the user to all the albums in the gallery that they are able to view.
    if (
      authenticated &&
      album &&
      canViewSpecialtyEventAlbums([album], albumFolder.event_settings)
    ) {
      onHierarchyViewsCreate([album.id]);
    }
  }, [authenticated, album]);

  useEffect(() => {
    if (uploading) {
      setStep(STEPS.UPLOADING);
    }
  }, [uploading]);

  useEffect(() => {
    if (success) {
      onUpdateUploadedStates(album);
    }

    // We have uploaded something
    if (success && step === STEPS.UPLOADING) {
      setStep(STEPS.HOME);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      albumUploadReset();
      openAlert({
        type: 'error',
        title: 'Something went wrong',
        message: error,
      });
    }
  }, [error]);

  const onFilesUpload = async (uploadedFiles, isVideos = false) => {
    const isVideoUpload = isVideoAlbum(album) || isVideos;
    const validFiles = isVideoUpload
      ? uploadedFiles.filter(file => isVideo(file))
      : uploadedFiles.filter(photoUploadFilter);

    if (!validFiles.length) {
      return; // Do nothing.
    }

    albumUploadReset();
    await onUpload(validFiles, album, isVideoUpload);
  };

  if (loading || !album) {
    return <FullScreenLoader />;
  }

  const renderStep = () => {
    switch (step) {
      case STEPS.PHOTO_UPLOAD:
        return <PhotoUploadStep album={album} onUpload={onFilesUpload} />;
      case STEPS.VIDEO_UPLOAD:
        return (
          <VideoUploadStep
            album={album}
            albumFolder={albumFolder}
            authenticated={authenticated}
            onBack={() => {
              setStep(STEPS.HOME);
            }}
            backButtonLabel="Back Home"
            onUpload={onFilesUpload}
            uploadOnly={false}
          />
        );
      case STEPS.UPLOADING:
        return (
          <UploadingStep
            current={current}
            total={total}
            error={error}
            success={success}
          />
        );
      case STEPS.LOADING:
        return <FullScreenLoader />;
      case STEPS.TARGET_UPLOAD_SUCCESS:
        return (
          <UploadSuccessStep
            album={album}
            albumFolder={albumFolder}
            authenticated={authenticated}
          />
        );
      case STEPS.HOME:
      default:
        return (
          <HomeStep
            album={album}
            authenticated={authenticated}
            uploadedTargets={uploadedTargets}
            onUpload={onFilesUpload}
            onVideoUpload={() => setStep(STEPS.VIDEO_UPLOAD)}
            canContinueUploading
            success={success}
            setStep={setStep}
          />
        );
    }
  };

  const isWeddings = isWeddingsContent(albumFolder);

  return (
    <SpecialtyEventUploadLayout
      isWeddings={isWeddings}
      backdropSrc="/background-circles-illustration.svg"
      contentClassName="standalone-specialty-event-upload-content"
      backdropClassName="standalone-specialty-event-upload-backdrop"
      headerClassName="standalone-specialty-event-upload-header"
    >
      <div className="standalone-specialty-event-upload-logo-container">
        <img src={getAsset('/waldo-logo.svg')} alt="Waldo" />
      </div>
      {renderStep()}
    </SpecialtyEventUploadLayout>
  );
};

const withAlbumForSpecialtyAlbumInvites = graphql(
  AlbumForSpecialtyAlbumInvites,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { album, loading } }) => ({
      loading,
      album,
    }),
  },
);

StandaloneSpecialtyEventUploadView.propTypes = propTypes;

export default compose(
  withRouter,
  withAlbumForSpecialtyAlbumInvites,
)(StandaloneSpecialtyEventUploadView);
